/**
 * THE WALL AFTER ITS SCOPE (DEMOLISH LAYERS round 2, 2026-10-14, docs/bim-plan/32 §3 E): the
 * assembly an existing wall has once its scope phase arrives. The layers its scope removes come
 * out (the core never moves: the painted-finish shift absorbs the change), then any NEW finish the
 * trowel put on a face in that phase (`scope.add`) goes on the way a painted finish does
 * (applyWallFinishes: the finish at its own thickness, its setting bed behind it). Resolved once
 * here so the geometry needs no model handle (the claddingSpecs precedent): the 3D / elevation
 * peel builds this wall, the plan and section band its NEW layers as new work.
 */
import type { AssemblyRecord, MaterialRecord } from './types'
import type { ResolvedElement } from './params'
import { normalizeScope, migrateWallScope, wallRemovedLayers, SCOPE_KEY } from './scope'
import { applyWallFinishes, type PaintedAssembly } from './wallCladding'

export interface WallScopeAfter {
  assembly: AssemblyRecord
  thicknessFt: number
  claddingSpecs: NonNullable<ResolvedElement['claddingSpecs']>
  /** how many of the after assembly's layers on each face are the NEW finish (face first) */
  added: { exterior: number; interior: number }
  /** the painted finish each face shows after (null = none) */
  finish: { exterior: string | null; interior: string | null }
}

export function wallScopeAfter(
  el: ResolvedElement,
  getMat: (id: string) => MaterialRecord | null | undefined,
  findByName: (name: string) => MaterialRecord | null | undefined,
): WallScopeAfter | null {
  if (el.category !== 'wall' || !el.assembly) return null
  const scope = migrateWallScope(normalizeScope(el.params[SCOPE_KEY]), el.assembly)
  if (!scope) return null
  const removed = new Set(wallRemovedLayers(el as never))
  const add = scope.add ?? {}
  if (!removed.size && !add.exterior && !add.interior) return null
  const asm = el.assembly as PaintedAssembly
  const cs = asm.coreBoundary?.start ?? 0
  const ce = asm.coreBoundary?.end ?? cs
  // 1. the removed layers out, the core held where it was
  let stripped: PaintedAssembly = stripWallLayers(asm, removed, 'peel')
  // 1b. an unsheathed wall getting a new exterior finish gets new sheathing under it (Matt: "many
  //     older homes don't have sheathing though. So if the siding is removed we'd have to add new
  //     sheathing"): the type's own exterior sheathing, new work in the scope phase
  let sheathed = 0
  if (add.exterior && el.params.sheathing === false) {
    const sub = sheathingLayers(asm)
    if (sub.length) {
      const t = sub.reduce((a, l) => a + (l.thicknessFt || 0), 0)
      stripped = {
        ...stripped,
        id: `${stripped.id}~sheath` as AssemblyRecord['id'],
        layers: [...sub.map((l) => ({ ...l })), ...stripped.layers],
        coreBoundary: { start: (stripped.coreBoundary?.start ?? 0) + sub.length, end: (stripped.coreBoundary?.end ?? 0) + sub.length },
        paintedFrom: stripped.paintedFrom ?? stripped,
        paintShiftFt: (stripped.paintedFrom ? stripped.paintShiftFt || 0 : 0) + t / 2,
      }
      sheathed = sub.length
    }
  }
  const specs: NonNullable<ResolvedElement['claddingSpecs']> = { ...(el.claddingSpecs ?? {}) }
  const finish = {
    exterior: typeof el.params.finishExterior === 'string' && el.params.finishExterior ? el.params.finishExterior : null,
    interior: typeof el.params.finishInterior === 'string' && el.params.finishInterior ? el.params.finishInterior : null,
  }
  for (const side of ['exterior', 'interior'] as const) {
    const fin = finish[side]
    const off = !!fin && asm.layers.some((l, i) => removed.has(i) && l.materialId === fin && (side === 'exterior' ? i < cs : i > ce))
    if (off) { finish[side] = null; delete specs[side] }
  }
  // 2. the new finishes on, as painted finishes on the stripped wall
  let after: PaintedAssembly = stripped
  const added = { exterior: sheathed, interior: 0 }
  if (add.exterior || add.interior) {
    const before = stripped.layers.length
    const r = applyWallFinishes(stripped, { exterior: add.exterior ?? null, interior: add.interior ?? null }, getMat, findByName)
    if (r.assembly !== stripped) {
      after = r.assembly
      const grew = after.layers.length - before
      // layers added in front of the core moved it: count the exterior ones by the core's shift
      const ext = (after.coreBoundary?.start ?? 0) - (stripped.coreBoundary?.start ?? 0)
      added.exterior = sheathed + ext
      added.interior = grew - ext
      for (const side of ['exterior', 'interior'] as const) {
        const sp = r.specs[side]
        if (sp) { specs[side] = sp; finish[side] = sp.materialId }
      }
    }
  }
  return {
    assembly: after,
    thicknessFt: after.layers.reduce((t, l) => t + (l.thicknessFt || 0), 0),
    claddingSpecs: specs,
    added,
    finish,
  }
}

/**
 * A wall assembly with some layers taken out, the core held exactly where it was: the
 * painted-finish shift (`paintShiftFt`, read by centerlineOffsetFt down the `paintedFrom` chain)
 * absorbs the change. Returns `asm` itself when nothing is taken out.
 */
export function stripWallLayers(asm: PaintedAssembly, removed: Set<number>, tag: string): PaintedAssembly {
  if (!removed.size) return asm
  const cs = asm.coreBoundary?.start ?? 0
  const ce = asm.coreBoundary?.end ?? cs
  let rExt = 0, rInt = 0, nExt = 0
  asm.layers.forEach((l, i) => {
    if (!removed.has(i)) return
    if (i < cs) { rExt += l.thicknessFt || 0; nExt++ } else if (i > ce) rInt += l.thicknessFt || 0
  })
  const shift0 = asm.paintedFrom ? asm.paintShiftFt || 0 : 0
  return {
    ...asm,
    id: `${asm.id}~${tag}` as AssemblyRecord['id'],
    layers: asm.layers.filter((_, i) => !removed.has(i)),
    coreBoundary: { start: cs - nExt, end: ce - nExt },
    paintedFrom: asm.paintedFrom ?? asm,
    paintShiftFt: shift0 - rExt / 2 + rInt / 2,
  }
}

/** the TYPE's exterior sheathing layers (substrate layers in front of the core), from the root of
 *  a painted assembly's chain */
export function sheathingLayers(asm: PaintedAssembly): AssemblyRecord['layers'] {
  let root: PaintedAssembly = asm
  while (root.paintedFrom) root = root.paintedFrom as PaintedAssembly
  const cs = root.coreBoundary?.start ?? 0
  return root.layers.filter((l, i) => i < cs && l.function === 'substrate')
}

/** SHEATHING OFF (the wall's `sheathing` param): the type's exterior sheathing left out */
export function withoutSheathing(asm: AssemblyRecord): AssemblyRecord | null {
  const cs = asm.coreBoundary?.start ?? 0
  const idx = new Set(asm.layers.map((l, i) => (i < cs && l.function === 'substrate' ? i : -1)).filter((i) => i >= 0))
  if (!idx.size) return null
  return stripWallLayers(asm as PaintedAssembly, idx, 'nosheath')
}
